// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';
import {VNode} from 'snabbdom';

import {CheckboxEl} from 'common/components/checkbox';
import {filterAttributes} from 'common/util';
import {SvgIconEl} from 'common/components/svg-icon';
import {SelectEl} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {confirm, prompt} from 'common/components/confirm';
import {THEME as ConfirmTheme} from 'common/components/confirm/types';
import {ItemsMenuEl} from 'common/components/items-menu';
import {
  EventHandler,
  ItemAndEventHandler,
  ItemHandler,
  ItemsMenuTheme,
  Section,
  SectionItemContextPanelTransform,
  SelectedTheme,
  SubLabelTheme,
  TagTheme,
  WrappedSection,
  WrappedSectionItem,
} from 'common/components/items-menu/types';
import {DropMenuEl} from 'common/components/drop-menu';
import {Directions, PositionParentType} from 'common/components/drop-menu/types';
import {ExperimentalTooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {TextHighlightEl} from 'common/components/text-highlight';
import {SIZE, TEXT_HIGHLIGHT_THEME} from 'common/components/text-highlight/types';
import {LocalizationStore} from 'common/i18n/store';
import {SvgIconType} from 'common/components/svg-icon/types';

const Label = ({label, labelMatches, showMatches}) => {
  if (labelMatches && showMatches) {
    return (
      <span sel='.label'>
        {labelMatches.map((part) => (
          <span sel='.match-part'>{part}</span>
        ))}
      </span>
    );
  }

  return <span sel='.label'>{label}</span>;
};

const UserControlWrapper = (
  {
    isFocused,
    isCheckbox,
    checkboxIcon,
    isCheckmark,
    isAnchorLink,
    item,
  }: {
    isCheckbox: boolean;
    checkboxIcon: SvgIconType;
    isCheckmark: boolean;
    isAnchorLink: boolean;
    isFocused: boolean;
    item: WrappedSectionItem;
  },
  children: VNode | VNode[],
) => {
  const sectionItem = item.wrapped;
  if (isCheckbox) {
    const isSpecialActionItem =
      sectionItem.isSelectAll || sectionItem.isSelectAllDefined || sectionItem.isSelectAllMatching;
    return (
      <CheckboxEl
        attrs={filterAttributes({
          elref: `checkbox`,
          checked: sectionItem.isSelected,
          /** if color is not provided, will be standard $blue1 */
          'checked-color': isSpecialActionItem ? `none` : sectionItem.color || null,
          disabled: sectionItem.isDisabled,
          'unchecked-icon': isSpecialActionItem ? `dash` : ``,
          'checked-icon': checkboxIcon,
          focused: isFocused,
        })}
      >
        {children}
      </CheckboxEl>
    );
  }

  if (isCheckmark) {
    return (
      <>
        <div sel='.checkmark-icon.item-icon'>
          <SvgIconEl attrs={{icon: `checkmark`, empty: !item.wrapped.isSelected}} />
        </div>
        {children}
      </>
    );
  }

  if (isAnchorLink) {
    return (
      <a sel='.list-option-link' attrs={{href: sectionItem.href, target: sectionItem.target}}>
        {children}
      </a>
    );
  }
  return <>{children}</>;
};

const SectionItemIcon = ({
  item,
  alignAllText,
  iconRenderer,
  selectedTheme,
  iconOnly,
  theme,
}: {
  item: WrappedSectionItem;
  alignAllText: boolean;
  iconRenderer: WrappedSection[`iconRenderer`];
  selectedTheme: SelectedTheme;
  iconOnly: boolean;
  theme: ItemsMenuTheme;
}) => {
  const sectionItem = item.wrapped;
  const listIcon = sectionItem.icon;

  if (typeof iconRenderer === `function`) {
    return <div sel='.custom-icon.item-icon'>{iconRenderer(sectionItem)}</div>;
  }

  return (
    (listIcon || alignAllText) && (
      <div
        sel='.option-icon.item-icon'
        attrs={{icon: listIcon}}
        class={{
          [`align-${sectionItem.iconSide || `left`}`]: true,
          'align-top':
            Boolean(sectionItem.subLabel) &&
            ![SelectedTheme.Checkbox, SelectedTheme.Checkmark].includes(selectedTheme) &&
            ![ItemsMenuTheme.CreateNewMenu].includes(theme),
          highlighted: sectionItem.highlightIcon,
          'icon-only': iconOnly,
        }}
      >
        <SvgIconEl attrs={{icon: listIcon, empty: !listIcon || sectionItem.hideIcon, color: sectionItem.color}} />
      </div>
    )
  );
};

const TAG_THEME_MAP = {
  [TagTheme.Default]: TEXT_HIGHLIGHT_THEME.INFORMATION,
  [TagTheme.Neutral]: TEXT_HIGHLIGHT_THEME.GENERIC,
};

const SectionItemLabel = ({
  item,
  showMatches,
  i18n,
}: {
  item: WrappedSectionItem;
  showMatches: boolean;
  i18n: LocalizationStore;
}) => {
  const sectionItem = item.wrapped;
  const {print} = i18n;

  const subLabel = print(sectionItem.subLabel);

  return (
    <>
      <div
        sel='.option-label-section'
        class={{
          [`sublabel-theme-${sectionItem.subLabelTheme}`]: Boolean(sectionItem.subLabelTheme),
          'has-sublabel': Boolean(subLabel),
        }}
      >
        <div sel='.option-label-container'>
          <div
            sel='.option-label'
            attrs={{
              title: print(sectionItem.label),
            }}
            class={{
              [`theme-${sectionItem.labelTheme}`]: Boolean(sectionItem.labelTheme),
              truncate: !sectionItem.wrapLabel,
            }}
          >
            {subLabel && sectionItem.subLabelTheme === SubLabelTheme.Breadcrumb && (
              <span sel='.option-label-path-prefix'>
                <Label label={subLabel} labelMatches={item.subLabelMatch?.matchParts} showMatches={showMatches} />
              </span>
            )}
            <Label
              label={print(sectionItem.label)}
              labelMatches={item.labelMatch?.matchParts}
              showMatches={showMatches}
            />
          </div>
          {Boolean(sectionItem.tag) && (
            <span sel='.tag-wrapper'>
              <TextHighlightEl
                text={sectionItem.tag}
                size={SIZE.SMALL}
                theme={TAG_THEME_MAP[sectionItem.tagTheme || TagTheme.Default]}
              />
            </span>
          )}
          {sectionItem.expansionToggle?.labelSuffix && (
            <div sel='.label-suffix.item-icon'>
              <ExperimentalTooltipEl
                attrs={{
                  placement: Placement.Top,
                  ...(sectionItem.expansionToggle.labelSuffix.tooltip ?? {}),
                }}
              />
              <SvgIconEl attrs={{icon: sectionItem.expansionToggle.labelSuffix.icon}} />
            </div>
          )}
        </div>
        {subLabel && sectionItem.subLabelTheme !== SubLabelTheme.Breadcrumb && (
          <div
            sel='.option-sub-label'
            attrs={{title: subLabel}}
            class={{
              [`theme-${sectionItem.subLabelTheme}`]: Boolean(sectionItem.subLabelTheme),
              truncate: !sectionItem.wrapSubLabel,
            }}
          >
            <Label label={subLabel} labelMatches={item.subLabelMatch?.matchParts} showMatches={showMatches} />
            {sectionItem.subLabelBadge && <span sel='.option-sub-label-badge'>{sectionItem.subLabelBadge}</span>}
          </div>
        )}
      </div>
      {sectionItem.sideLabel && <span sel='.option-side-label'>{sectionItem.sideLabel}</span>}
    </>
  );
};

const SectionItemAction = ({
  item,
  isFocused,
  isPillFocused,
  actionsRenderer,
  onClick,
  onMouseEnter,
  onMouseLeave,
  fullyShownSections,
}: {
  item: WrappedSectionItem;
  isFocused: boolean;
  isPillFocused: boolean;
  actionsRenderer: WrappedSection[`actionsRenderer`];
  onClick: ItemAndEventHandler;
  onMouseEnter: ItemHandler;
  onMouseLeave: ItemHandler;
  fullyShownSections: Obj<boolean>;
}) => {
  const sectionItem = item.wrapped;
  if (typeof actionsRenderer === `function`) {
    return <div sel='.custom-actions'>{actionsRenderer(sectionItem)}</div>;
  }

  if (sectionItem.hasPill) {
    return (
      <div
        sel='.right-side-icon.pill'
        class={{
          focused: isFocused && isPillFocused,
          disabled: sectionItem.isPillDisabled,
        }}
        on={{
          click: !sectionItem.isPillDisabled ? (ev) => onClick?.(item, ev) : null,
          mouseenter: () => onMouseEnter?.(item),
          mouseleave: () => onMouseLeave?.(item),
        }}
      >
        <span>{sectionItem.pillLabel}</span>
        <div sel='.pill-caret.item-icon'>
          <SvgIconEl attrs={{icon: `caret-right`}} />
        </div>
      </div>
    );
  }

  const hasSubsections = sectionItem.subSections && sectionItem.subSections.length;
  const caretDisabled = sectionItem.hasCaret === false;
  const shouldShowCaret = !caretDisabled && (hasSubsections || sectionItem.hasCaret);
  if (shouldShowCaret) {
    return (
      <div
        sel='.right-side-icon.item-icon'
        class={{
          persistent: sectionItem.persistCaret,
        }}
      >
        <SvgIconEl attrs={{icon: sectionItem.caretStyle === `arrow` ? `arrow-right` : `caret-right`}} />
      </div>
    );
  }

  if (sectionItem.isExpander && sectionItem.tooltip) {
    return (
      <div sel='.right-side-icon.item-icon.persistent'>
        <ExperimentalTooltipEl
          attrs={{
            placement: Placement.RightCenter,
            ...(sectionItem.tooltip ?? {}),
          }}
        />
        <SvgIconEl attrs={{icon: `info`}} />
      </div>
    );
  }

  if (sectionItem.expansionToggle) {
    return (
      <div sel='.right-side-icon.item-icon.persistent'>
        <SvgIconEl
          attrs={{
            icon: fullyShownSections[item.sectionIndex] ? `caret-down` : `caret-right`,
          }}
        />
      </div>
    );
  }

  return null;
};

export const SectionItemEl = (
  {
    item,
    theme,
    selectedTheme,
    checkboxIcon,
    alignAllText,
    isFocused,
    isPillFocused,
    showMatches,
    iconRenderer,
    actionsRenderer,
    onActionClick,
    onActionMouseEnter,
    onActionMouseLeave,
    fullyShownSections,
    i18n,
    renderOverflowMenu = false,
    onDeleteTag,
    onEditTag,
    disableSelectedItem = true,
  }: {
    item: WrappedSectionItem;
    theme: ItemsMenuTheme;
    selectedTheme: SelectedTheme;
    checkboxIcon: SvgIconType;
    alignAllText: boolean;
    isFocused: boolean;
    isPillFocused: boolean;
    showMatches: boolean;
    iconRenderer: WrappedSection[`iconRenderer`];
    actionsRenderer: WrappedSection[`actionsRenderer`];
    onActionClick: ItemAndEventHandler;
    onActionMouseEnter: ItemHandler;
    onActionMouseLeave: ItemHandler;
    fullyShownSections: Obj<boolean>;
    i18n: LocalizationStore;
    renderOverflowMenu?: boolean;
    onDeleteTag?: (tag: string) => void | Promise<void>;
    onEditTag?: (tag: string, newTag: string) => void | Promise<void>;
    disableSelectedItem?: boolean;
  },
  sectionItemSubMenuChild: VNode,
) => {
  const sectionItem = item.wrapped;
  const {t} = i18n;
  let isCheckbox = false;
  let isCheckmark = false;
  if (!sectionItem.isExpander && !sectionItem.expansionToggle && !sectionItem.isControlItem) {
    isCheckbox = selectedTheme === SelectedTheme.Checkbox;
    isCheckmark = selectedTheme === SelectedTheme.Checkmark;
  }

  isCheckmark = isCheckmark || !disableSelectedItem;

  const isAnchorLink = Boolean(sectionItem.href);
  const hasLabel = Boolean(item.wrapped.label);

  return (
    <UserControlWrapper
      isCheckbox={isCheckbox && !item.wrapped.isSpecialAction}
      checkboxIcon={checkboxIcon}
      isCheckmark={isCheckmark}
      isAnchorLink={isAnchorLink}
      isFocused={isFocused}
      item={item}
    >
      <SectionItemIcon
        item={item}
        theme={theme}
        alignAllText={alignAllText}
        iconRenderer={iconRenderer}
        selectedTheme={selectedTheme}
        iconOnly={!hasLabel}
      />
      {hasLabel && <SectionItemLabel item={item} showMatches={showMatches} i18n={i18n} />}
      <SectionItemAction
        item={item}
        isFocused={isFocused}
        isPillFocused={isPillFocused}
        onClick={onActionClick}
        onMouseEnter={onActionMouseEnter}
        onMouseLeave={onActionMouseLeave}
        actionsRenderer={actionsRenderer}
        fullyShownSections={fullyShownSections}
      />
      {renderOverflowMenu && (
        <div
          sel='.overflow-menu-wrapper.show-on-hover'
          on={{
            click: (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
            },
          }}
        >
          <SelectEl
            sel='.overflow-menu'
            attrs={{
              theme: SelectTheme.Tertiary,
              icon: {leftIcon: `hor-ellipsis`},
              'icon-only': true,
              'menu-width': 120,
              'menu-alignment': Directions.Right,
              'disable-search': true,
              tooltip: {text: t(`lexicon.events.actions.manage-tag-tooltip`)},
              elref: `overflowMenu_${item.wrapped.value}`,
            }}
            props={{
              sections: [
                {
                  items: [
                    {
                      icon: `trashcan`,
                      label: t(`common.delete`),
                      value: `delete`,
                    },
                    {
                      icon: `edit`,
                      label: t(`common.edit`),
                      value: `edit`,
                    },
                  ],
                },
              ],
            }}
            on={{
              change: async (ev: any) => {
                if (
                  ev.detail.item?.value === `delete` &&
                  (await confirm({
                    theme: ConfirmTheme.DELETE,
                    title: t(`lexicon.events.actions.delete-tag-title`),
                    description: t(`lexicon.events.actions.delete-tag-description`),
                    'confirm-button-label': t(`common.delete`),
                    'description-allow-newline': true,
                  }))
                ) {
                  onDeleteTag?.(item.wrapped.value);
                } else if (ev.detail.item?.value === `edit`) {
                  const result = await prompt({
                    title: t(`lexicon.events.actions.edit-tag-title`),
                    description: t(`lexicon.events.actions.edit-tag-description`),
                    'confirm-button-label': t(`common.save`),
                    'show-input': true,
                    'initial-input-value': item.wrapped.value,
                  });

                  if (result.didConfirm && result.inputValue?.trim()) {
                    onEditTag?.(item.wrapped.value, result.inputValue.trim());
                  }
                }
              },
            }}
          />
        </div>
      )}
      {sectionItemSubMenuChild}
    </UserControlWrapper>
  );
};

export const SectionItemSubMenuEl = ({
  alignAllText,
  selectedTheme,
  contextPanelTransform,
  keydownProxyEl,
  onNavigateBack,
  onClickItem,
  onFocusItem,
  onMouseEnter,
  sections,
  theme,
}: {
  alignAllText: boolean;
  selectedTheme: SelectedTheme;
  contextPanelTransform: SectionItemContextPanelTransform;
  keydownProxyEl: HTMLElement;
  onNavigateBack: () => void;
  onClickItem: EventHandler;
  onFocusItem: EventHandler;
  onMouseEnter: (ev: MouseEvent) => void;
  sections?: Section[];
  theme?: ItemsMenuTheme;
}) => {
  return (
    <DropMenuEl
      sel='.option-subsections'
      attrs={{
        open: true,
        offset: 10,
        direction: Directions.Right,
      }}
      props={{
        // place the dropdown relative to the parent element as opposed to its offset parent
        positionParent: PositionParentType.ParentElement,
      }}
      on={{
        mouseenter: onMouseEnter,
        click: (ev) => {
          ev.stopPropagation();
        },
      }}
    >
      <ItemsMenuEl
        attrs={{
          elref: `nestedList`,
          'selected-theme': selectedTheme,
          'align-all-text': alignAllText,
          'disable-auto-focus': true,
          theme,
        }}
        props={{
          sections,
          contextPanelTransform,
          keydownProxyEl,
        }}
        on={{
          navigateBack: () => onNavigateBack(),
          clickItem: onClickItem,
          focusItem: onFocusItem,
        }}
      />
    </DropMenuEl>
  );
};
